import { AdditiveBlending, AudioAnalyser, Color, ColorRepresentation, DoubleSide, Mesh, PlaneGeometry, ShaderMaterial } from 'three';

const DEFAULT_WIDTH = 0.4;
const DEFAULT_HEIGHT = 0.2;
const DEFAULT_AMPLITUDE = 0.158;
const DEFAULT_GLOW = 1.2;
const DEFAULT_COLOR = 0x33aaff;
const DEFAULT_THICKNESS = 0.013;
const DEFAULT_LINE_LENGTH = 0.083;
const DEFAULT_SMILE = 0.1;
const DEFAULT_VIBRATION = 0.008;
const VOLUME_LERP_SPEED = 15.0;
const SILENT_DECAY = 5.0;

export interface WaveformOptions {
    width?: number;
    height?: number;
    amplitude?: number;
    glow?: number;
    color?: ColorRepresentation;
    thickness?: number;
    lineLength?: number;
    smile?: number;
    vibration?: number;
}

const VERT = /* glsl */ `
varying vec2 vUv;

void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const FRAG = /* glsl */ `
uniform vec3 uColor;
uniform float uGlow;
uniform float uVolume;
uniform float uAmplitude;
uniform float uTime;
uniform float uVibration;
uniform float uThickness;
uniform float uPlaneWidth;
uniform float uPlaneHeight;
uniform float uLineLength;
uniform float uSmile;

varying vec2 vUv;

void main() {
    float px = (vUv.x - 0.5) * uPlaneWidth;
    float py = (vUv.y - 0.5) * uPlaneHeight;
    float normX = (vUv.x - 0.5) * 2.0;

    float smileOffset = (normX * normX) * uSmile;
    py -= smileOffset;

    float envelope = 1.0 - (normX * normX);

    float wave = sin(normX * 15.0 - uTime * 20.0) * 0.5
               + cos(normX * 25.0 + uTime * 35.0) * 0.5;
    py += wave * uVolume * uVibration * envelope;

    float talkOpen = uVolume * uAmplitude * envelope;

    float dX = max(0.0, abs(px) - uLineLength);
    float dY = max(0.0, abs(py) - talkOpen);
    float dist = length(vec2(dX, dY));

    float aa = 0.003;
    float core = 1.0 - smoothstep(uThickness - aa, uThickness, dist);

    float glowThickness = uThickness + uGlow * 0.03;
    float glow = 1.0 - smoothstep(0.0, glowThickness, dist);
    glow = pow(glow, 2.0);

    float alpha = core + glow * 0.6;
    vec3 finalColor = uColor * (1.0 + core * 0.5);

    gl_FragColor = vec4(finalColor, clamp(alpha, 0.0, 1.0));
}
`;

export class Waveform extends Mesh {
    private shaderMaterial: ShaderMaterial;

    constructor(opts: WaveformOptions = {}) {
        const width = opts.width ?? DEFAULT_WIDTH;
        const height = opts.height ?? DEFAULT_HEIGHT;
        const color = new Color(opts.color ?? DEFAULT_COLOR);

        const geometry = new PlaneGeometry(width, height, 1, 1);

        const material = new ShaderMaterial({
            uniforms: {
                uVolume: { value: 0 },
                uAmplitude: { value: opts.amplitude ?? DEFAULT_AMPLITUDE },
                uTime: { value: 0 },
                uColor: { value: color },
                uGlow: { value: opts.glow ?? DEFAULT_GLOW },
                uThickness: { value: opts.thickness ?? DEFAULT_THICKNESS },
                uPlaneWidth: { value: width },
                uPlaneHeight: { value: height },
                uLineLength: { value: opts.lineLength ?? DEFAULT_LINE_LENGTH },
                uSmile: { value: opts.smile ?? DEFAULT_SMILE },
                uVibration: { value: opts.vibration ?? DEFAULT_VIBRATION },
            },
            vertexShader: VERT,
            fragmentShader: FRAG,
            transparent: true,
            depthWrite: false,
            side: DoubleSide,
            blending: AdditiveBlending,
        });

        super(geometry, material);
        this.shaderMaterial = material;
        this.renderOrder = 100;
    }

    updateFrequency(analyser: AudioAnalyser | null, dt: number): void {
        this.shaderMaterial.uniforms.uTime.value += dt;

        if (!analyser) {
            this.shaderMaterial.uniforms.uVolume.value *= Math.exp(-SILENT_DECAY * dt);
            return;
        }

        const data = analyser.getFrequencyData() as Uint8Array;
        const limit = Math.floor(data.length * 0.5);
        let sum = 0;
        for (let i = 0; i < limit; i++) sum += data[i];
        const avg = (sum / limit) / 255.0;

        const cur = this.shaderMaterial.uniforms.uVolume.value;
        const alpha = 1.0 - Math.exp(-VOLUME_LERP_SPEED * dt);
        this.shaderMaterial.uniforms.uVolume.value = cur + (avg - cur) * alpha;
    }

    dispose(): void {
        this.geometry.dispose();
        this.shaderMaterial.dispose();
    }
}
